import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Chip, Divider,
    CircularProgress, Alert, Stack, useMediaQuery, useTheme
} from '@mui/material';
import {
    Clock, Calendar, CreditCard, Tag, Zap,
    Ban, Package, BedDouble
} from 'lucide-react';
import { getRetreatSchedule } from '../../../Services/Retreats/RetreatApis';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (dateString) => {
    if (!dateString) return 'Flexible / Open';
    try {
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return 'Flexible / Open';
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
        return 'Flexible / Open';
    }
};

// ─── Reusable primitives ──────────────────────────────────────────────────────

/** Section heading with icon */
const SectionTitle = ({ icon: Icon, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Icon size={15} color="#FFBD65" strokeWidth={2.2} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', letterSpacing: '0.02em' }}>
            {children}
        </Typography>
    </Box>
);

/**
 * Label → Value row.
 * On mobile (xs) the label sits above the value.
 * On sm+ the label is fixed-width inline with the value.
 */
const InfoRow = ({ label, children, noBorder }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            py: 1.2,
            borderBottom: noBorder ? 'none' : '1px solid #F1F5F9',
            gap: { xs: 0.25, sm: 0 },
        }}
    >
        <Typography
            variant="caption"
            sx={{
                color: '#94A3B8',
                fontWeight: 500,
                minWidth: { sm: 140 },
                flexShrink: 0,
            }}
        >
            {label}
        </Typography>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
            {typeof children === 'string' || typeof children === 'number' ? (
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155', textAlign: { xs: 'left', sm: 'right' }, width: '100%' }}>
                    {children}
                </Typography>
            ) : (
                children
            )}
        </Box>
    </Box>
);

/** Thin white card */
const InfoCard = ({ children, sx }) => (
    <Paper
        elevation={0}
        sx={{ border: '1px solid #E8EEF4', borderRadius: '10px', bgcolor: '#FFFFFF', overflow: 'hidden', ...sx }}
    >
        {children}
    </Paper>
);

/** Coloured card top-bar */
const CardHeader = ({ icon: Icon, title, chip }) => (
    <Box
        sx={{
            px: { xs: 2, sm: 2.5 },
            py: 2,
            borderBottom: '1px solid #F1F5F9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon size={14} color="#FFBD65" />
            <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}
            >
                {title}
            </Typography>
        </Box>
        {chip}
    </Box>
);

/** Small status badge */
const StatusBadge = ({ active, trueLabel = 'Yes', falseLabel = 'No' }) => (
    <Chip
        label={active ? trueLabel : falseLabel}
        size="small"
        sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600,
            bgcolor: active ? '#F0FDF4' : '#F8FAFC',
            color: active ? '#16A34A' : '#94A3B8',
            border: `1px solid ${active ? '#BBF7D0' : '#E2E8F0'}`,
        }}
    />
);

// ─── Main component ───────────────────────────────────────────────────────────

const SchedulePricingTab = ({ retreat }) => {
    const [retreatSchedule, setRetreatSchedule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchData = async () => {
            if (!retreat?.id) return;
            try {
                setLoading(true);
                const response = await getRetreatSchedule(retreat.id);
                if (response?.success) {
                    setRetreatSchedule(response.data);
                } else {
                    setError('Failed to fetch schedule and pricing information.');
                }
            } catch (err) {
                console.error('Error fetching retreat schedule:', err);
                setError('An error occurred while fetching schedule information.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [retreat?.id]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={28} sx={{ color: '#FFBD65' }} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>;
    }

    if (!retreatSchedule) {
        return (
            <InfoCard sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    No schedule and pricing information available.
                </Typography>
            </InfoCard>
        );
    }

    const paymentMethods = Array.isArray(retreatSchedule?.paymentMethods)
        ? retreatSchedule.paymentMethods
        : typeof retreatSchedule?.paymentMethods === 'string'
            ? JSON.parse(retreatSchedule.paymentMethods)
            : [];

    const getOpenSpots = (id) =>
        retreatSchedule.accommodationOpenSpots?.find(s => s.accommodationId === id) ?? null;

    const getSeasonalPricing = (id) =>
        retreatSchedule.accommodationSeasonalPricing?.filter(p => p.accommodationId === id) ?? [];

    const unavailableDates =
        retreatSchedule.unavailableDates || retreatSchedule.RetreatUnavailableDates || [];

    const cardPx = { xs: 2.5, sm: 3 };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

            {/* ── 1. Schedule Overview ──────────────────────────────────────── */}
            <Box>
                <SectionTitle icon={Clock}>Schedule Overview</SectionTitle>
                <InfoCard>
                    <Box sx={{ px: cardPx, py: 1 }}>
                        <InfoRow label="Run Type">{retreatSchedule.runType ?? '—'}</InfoRow>
                        <InfoRow label="Booking Instantly">
                            <Stack direction="row" spacing={1} alignItems="center">
                                <StatusBadge active={retreatSchedule.bookingInstantly} trueLabel="True" falseLabel="False" />
                                <Chip
                                    label={retreatSchedule.bookingInstantly ? "Book now" : "Request a Reservation"}
                                    size="small"
                                    sx={{
                                        height: 20, fontSize: '0.7rem', fontWeight: 600,
                                        bgcolor: retreatSchedule.bookingInstantly ? '#F0FDF4' : '#EFF6FF',
                                        color: retreatSchedule.bookingInstantly ? '#16A34A' : '#1D4ED8',
                                        border: `1px solid ${retreatSchedule.bookingInstantly ? '#BBF7D0' : '#BFDBFE'}`,
                                    }}
                                />
                            </Stack>
                        </InfoRow>
                        <InfoRow label="Duration">
                            {retreatSchedule.durationDays ? `${retreatSchedule.durationDays} Days` : '—'}
                        </InfoRow>
                        <InfoRow label="Participants">
                            {retreatSchedule.minParticipants ?? '—'} – {retreatSchedule.maxParticipants ?? '—'}
                        </InfoRow>
                        <InfoRow label="Currency">{retreatSchedule.currency ?? '—'}</InfoRow>
                        <InfoRow label="Min Booking Days">
                            {retreatSchedule.minimumBookingDays
                                ? `${retreatSchedule.minimumBookingDays} Days`
                                : '—'}
                        </InfoRow>
                        <InfoRow label="Open All Year">
                            <StatusBadge active={retreatSchedule.openAllYearRound} />
                        </InfoRow>
                        <InfoRow label="Fixed Start Day">
                            <StatusBadge active={retreatSchedule.fixedStartDayEnabled} />
                        </InfoRow>
                        <InfoRow label="Eco Friendly">
                            <StatusBadge active={retreatSchedule.ecoFriendlyEnabled} />
                        </InfoRow>
                        <InfoRow label="Allowed Start Days" noBorder>
                            {Array.isArray(retreatSchedule.dayOfWeek) && retreatSchedule.dayOfWeek.length > 0 ? (
                                <Stack direction="row" flexWrap="wrap" gap={0.75} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }} sx={{ width: '100%' }}>
                                    {retreatSchedule.dayOfWeek.map(day => (
                                        <Chip
                                            key={day}
                                            label={day}
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontSize: '0.7rem',
                                                bgcolor: '#F8FAFC',
                                                border: '1px solid #E2E8F0',
                                                color: '#475569',
                                                fontWeight: 500,
                                            }}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                    {retreatSchedule.dayOfWeek || 'Any day'}
                                </Typography>
                            )}
                        </InfoRow>
                    </Box>
                </InfoCard>
            </Box>

            {/* ── 2. Accommodations & Pricing ───────────────────────────────── */}
            {retreatSchedule.accommodations?.length > 0 && (
                <Box>
                    <SectionTitle icon={BedDouble}>Accommodations & Pricing</SectionTitle>
                    <Grid container spacing={2}>
                        {retreatSchedule.accommodations.map((acc) => {
                            const spots = getOpenSpots(acc.id);
                            const seasonal = getSeasonalPricing(acc.id);
                            const available = spots ? spots.defaultOpenSpots - spots.bookedSpots : null;

                            return (
                                <Grid size={{ xs: 12, md: 12 }} key={acc.id}>
                                    <InfoCard sx={{ height: '100%' }}>
                                        <CardHeader
                                            icon={BedDouble}
                                            title={acc.title}
                                            chip={
                                                available !== null ? (
                                                    <Chip
                                                        label={`${available} / ${spots.defaultOpenSpots} spots`}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.7rem',
                                                            fontWeight: 600,
                                                            bgcolor: available > 0 ? '#F0FDF4' : '#FFF1F2',
                                                            color: available > 0 ? '#16A34A' : '#E11D48',
                                                            border: `1px solid ${available > 0 ? '#BBF7D0' : '#FECDD3'}`,
                                                        }}
                                                    />
                                                ) : null
                                            }
                                        />
                                        <Box sx={{ px: cardPx, py: 2 }}>
                                            <InfoRow label="Standard Price">
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                                    {acc.price}{' '}
                                                    <Typography component="span" variant="caption" color="text.secondary">
                                                        / {acc.priceType?.replace('_', ' ') || '-'}
                                                    </Typography>
                                                </Typography>
                                            </InfoRow>
                                            {acc.earlyBirdPrice && (
                                                <InfoRow label="Early Bird Price">
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#B45309' }}>
                                                        {acc.earlyBirdPrice}
                                                    </Typography>
                                                </InfoRow>
                                            )}
                                            {acc.flashSalePrice && (
                                                <InfoRow label="Flash Sale Price">
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0E7490' }}>
                                                        {acc.flashSalePrice}
                                                    </Typography>
                                                </InfoRow>
                                            )}
                                            {acc.currency && (
                                                <InfoRow label="Currency">
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0E7490' }}>
                                                        {acc.currency}
                                                    </Typography>
                                                </InfoRow>
                                            )}
                                            <InfoRow label="Max Occupancy">{acc.maxOccupancy} person(s)</InfoRow>
                                            <InfoRow label="Deposit Required" noBorder={seasonal.length === 0}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <StatusBadge active={acc.depositRequired} />
                                                    {acc.setDeposit && acc.depositPercentage && (
                                                        <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                                            ({acc.depositPercentage}%)
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </InfoRow>

                                            {/* Seasonal Overrides — tabular */}
                                            {seasonal.length > 0 && (
                                                <Box sx={{ mt: 1.5 }}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: '#94A3B8',
                                                            fontWeight: 700,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.05em',
                                                            display: 'block',
                                                            mb: 1,
                                                        }}
                                                    >
                                                        Seasonal Overrides
                                                    </Typography>
                                                    {/* Header row */}
                                                    <Box
                                                        sx={{
                                                            display: 'grid',
                                                            gridTemplateColumns: '1fr auto',
                                                            px: 1.5,
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        <Typography variant="caption" sx={{ color: '#CBD5E1', fontWeight: 600 }}>Period</Typography>
                                                        <Typography variant="caption" sx={{ color: '#CBD5E1', fontWeight: 600 }}>Price</Typography>
                                                    </Box>
                                                    <Stack spacing={0.5}>
                                                        {seasonal.map((rate, idx) => (
                                                            <Box
                                                                key={rate.id}
                                                                sx={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: '1fr auto',
                                                                    py: 0.6,
                                                                    px: 1.5,
                                                                    bgcolor: idx % 2 === 0 ? '#F8FAFC' : '#FFFFFF',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid #F1F5F9',
                                                                }}
                                                            >
                                                                <Typography variant="caption" sx={{ color: '#475569' }}>
                                                                    {formatDate(rate.startDate)} – {formatDate(rate.endDate)}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                                                    {acc.currency} {rate.price}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            )}
                                        </Box>
                                    </InfoCard>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            )}

            {/* ── 3. Pricing & Registration Periods ────────────────────────── */}
            {retreatSchedule.priceDates?.length > 0 && (
                <Box>
                    <SectionTitle icon={Calendar}>Pricing & Registration Periods</SectionTitle>
                    <InfoCard>
                        {/* Table header — hidden on mobile */}
                        {!isMobile && (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1.8fr)',
                                    px: 3,
                                    py: 1.5,
                                    bgcolor: '#F8FAFC',
                                    borderBottom: '1px solid #F1F5F9',
                                    gap: 2,
                                }}
                            >
                                {['Retreat Dates', 'Registration Deadline', 'Offers'].map(h => (
                                    <Typography key={h} variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {h}
                                    </Typography>
                                ))}
                            </Box>
                        )}

                        {retreatSchedule.priceDates.map((date, idx) => (
                            <Box key={date.id}>
                                {isMobile ? (
                                    /* Mobile: InfoRow layout */
                                    <Box sx={{ px: cardPx, py: 2 }}>
                                        <InfoRow label="Retreat Dates">
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                                {formatDate(date.startDate)} → {formatDate(date.endDate)}
                                            </Typography>
                                        </InfoRow>
                                        <InfoRow label="Reg. Deadline">
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: date.registrationCloseDate ? '#DC2626' : '#64748B' }}>
                                                {date.registrationCloseDate ? formatDate(date.registrationCloseDate) : 'Open'}
                                            </Typography>
                                        </InfoRow>
                                        <InfoRow label="Offers" noBorder>
                                            <PeriodOffers date={date} />
                                        </InfoRow>
                                    </Box>
                                ) : (
                                    /* Desktop: 3-column grid row */
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1.8fr)',
                                            px: 3,
                                            py: 1.75,
                                            gap: 2,
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                            {formatDate(date.startDate)} → {formatDate(date.endDate)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: date.registrationCloseDate ? '#DC2626' : '#64748B' }}>
                                            {date.registrationCloseDate ? formatDate(date.registrationCloseDate) : 'Open'}
                                        </Typography>
                                        <PeriodOffers date={date} />
                                    </Box>
                                )}
                                {idx < retreatSchedule.priceDates.length - 1 && <Divider />}
                            </Box>
                        ))}
                    </InfoCard>
                </Box>
            )}

            {/* ── 4. Payment & Cancellation ─────────────────────────────────── */}
            <Grid container spacing={3}>
                {/* Payment Rules */}
                <Grid size={12}>
                    <SectionTitle icon={CreditCard}>Payment Rules</SectionTitle>
                    <InfoCard>
                        <Box sx={{ px: cardPx, py: 2 }}>
                            <InfoRow label="Full Payment">
                                <StatusBadge active={retreatSchedule.fullPaymentAllowed} trueLabel="Allowed" falseLabel="Not Allowed" />
                            </InfoRow>
                            <InfoRow label="Deposit Payment">
                                <StatusBadge active={retreatSchedule.depositAllowed} trueLabel="Allowed" falseLabel="Not Allowed" />
                            </InfoRow>
                            <InfoRow label="Transfer Allowed">
                                <StatusBadge active={retreatSchedule.transferAllowed} />
                            </InfoRow>
                            <InfoRow label="Payment Methods" noBorder>
                                {paymentMethods.length > 0 ? (
                                    <Stack direction="row" flexWrap="wrap" gap={0.75} justifyContent="flex-start" sx={{ width: '100%' }}>
                                        {paymentMethods.map(method => (
                                            <Chip
                                                key={method}
                                                label={method.replace('_', ' ')}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.7rem',
                                                    textTransform: 'capitalize',
                                                    fontWeight: 500,
                                                    bgcolor: '#F8FAFC',
                                                    border: '1px solid #E2E8F0',
                                                    color: '#475569',
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                ) : (
                                    <Typography variant="body2" sx={{ color: '#94A3B8' }}>—</Typography>
                                )}
                            </InfoRow>
                        </Box>
                    </InfoCard>
                </Grid>

                {/* Cancellation Policy */}
                <Grid size={12}>
                    <SectionTitle icon={Ban}>Cancellation Policy</SectionTitle>
                    <InfoCard>
                        <Box sx={{ px: cardPx, py: 2 }}>
                            <InfoRow label="Policy Type">
                                {(() => {
                                    const rawType = retreatSchedule.cancellationPolicyType ? String(retreatSchedule.cancellationPolicyType).toLowerCase() : '';
                                    let bg = '#F8FAFC', color = '#475569', border = '#E2E8F0';
                                    if (rawType === 'free') { bg = '#E8F5E9'; color = '#2E7D32'; border = '#C8E6C9'; }
                                    else if (rawType === 'flexible') { bg = '#E3F2FD'; color = '#1565C0'; border = '#BBDEFB'; }
                                    else if (rawType === 'moderate') { bg = '#FFF3E0'; color = '#E65100'; border = '#FFE0B2'; }
                                    else if (rawType === 'custom') { bg = '#F3E5F5'; color = '#7B1FA2'; border = '#E1BEE7'; }

                                    return (
                                        <Chip
                                            label={retreatSchedule.cancellationPolicyType ? String(retreatSchedule.cancellationPolicyType).toUpperCase() : 'N/A'}
                                            size="small"
                                            sx={{
                                                height: 22,
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                bgcolor: bg,
                                                border: `1px solid ${border}`,
                                                color: color,
                                            }}
                                        />
                                    );
                                })()}
                            </InfoRow>
                            {String(retreatSchedule.cancellationPolicyType).toLowerCase() === 'custom' && (
                                <>
                                    <InfoRow label="Deposit Refund">
                                        <StatusBadge active={retreatSchedule.depositRefundEnabled} trueLabel="Enabled" falseLabel="Disabled" />
                                    </InfoRow>
                                    <InfoRow label="100% Deposit Refund">
                                        {retreatSchedule.deposit100PercentRefundDays != null ? `${retreatSchedule.deposit100PercentRefundDays} Days before check-in` : '—'}
                                    </InfoRow>
                                    <InfoRow label="50% Deposit Refund">
                                        {retreatSchedule.deposit50PercentRefundDays != null ? `${retreatSchedule.deposit50PercentRefundDays} Days before check-in` : '—'}
                                    </InfoRow>
                                    <InfoRow label="Balance on Arrival">
                                        <StatusBadge active={retreatSchedule.allowRemainingBalanceOnArrival} trueLabel="Allowed" falseLabel="Not Allowed" />
                                    </InfoRow>
                                    <InfoRow label="Remaining Balance Due">
                                        {retreatSchedule.remainingBalanceDueBeforeDays != null ? `${retreatSchedule.remainingBalanceDueBeforeDays} Days before check-in` : '—'}
                                    </InfoRow>
                                </>
                            )}
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Policy Details
                            </Typography>


                            <Box
                                sx={{
                                    color: '#475569',
                                    fontSize: '0.82rem',
                                    lineHeight: 1.65,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                    whiteSpace: 'normal',
                                    '& p': { mb: 1, wordBreak: 'break-word', overflowWrap: 'anywhere' },
                                    '& ul, & ol': { pl: '1.2rem', mb: 1, '& li': { mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' } },
                                    '& *': {
                                        fontFamily: 'inherit !important',
                                        fontSize: 'inherit !important',
                                        color: 'inherit !important',
                                        wordBreak: 'break-word !important',
                                        overflowWrap: 'anywhere !important',
                                        maxWidth: '100%',
                                    },
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: retreatSchedule.cancellationPolicyText
                                        || '<p style="color:#94A3B8">No additional policy text provided.</p>',
                                }}
                            />
                        </Box>
                    </InfoCard>
                </Grid>
            </Grid>

            {/* ── 5. Blackout / Unavailable Dates ──────────────────────────── */}
            {unavailableDates.length > 0 && (
                <Box>
                    <SectionTitle icon={Ban}>Blackout & Unavailable Dates</SectionTitle>
                    <InfoCard>
                        {/* Header */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                px: cardPx,
                                py: 1.5,
                                bgcolor: '#F8FAFC',
                                borderBottom: '1px solid #F1F5F9',
                                gap: 2,
                            }}
                        >
                            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Start Date</Typography>
                            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: { xs: 'none', sm: 'block' } }}>End Date</Typography>
                        </Box>
                        {unavailableDates.map((date, idx) => (
                            <Box key={date.id}>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                        px: cardPx,
                                        py: 1.5,
                                        gap: 2,
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#BE123C' }}>
                                        {formatDate(date.startDate)}
                                        {/* On mobile, show both inline */}
                                        <Typography component="span" variant="body2" sx={{ display: { xs: 'inline', sm: 'none' }, color: '#94A3B8', mx: 0.5 }}>→</Typography>
                                        <Typography component="span" variant="body2" sx={{ display: { xs: 'inline', sm: 'none' }, color: '#BE123C' }}>
                                            {formatDate(date.endDate)}
                                        </Typography>
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#BE123C', display: { xs: 'none', sm: 'block' } }}>
                                        {formatDate(date.endDate)}
                                    </Typography>
                                </Box>
                                {idx < unavailableDates.length - 1 && <Divider />}
                            </Box>
                        ))}
                    </InfoCard>
                </Box>
            )}

            {/* ── 6. Add-ons ───────────────────────────────────────────────── */}
            {retreatSchedule.addons?.length > 0 && (
                <Box>
                    <SectionTitle icon={Package}>Available Add-ons</SectionTitle>
                    <InfoCard>
                        {/* Table header */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr auto', sm: '1fr auto auto auto' },
                                px: cardPx,
                                py: 1.5,
                                bgcolor: '#F8FAFC',
                                borderBottom: '1px solid #F1F5F9',
                                gap: { xs: 1, sm: 2 },
                                alignItems: 'center',
                            }}
                        >
                            {['Add-on Name', 'Price', 'Qty', 'Type'].map((h, i) => (
                                <Typography
                                    key={h}
                                    variant="caption"
                                    sx={{
                                        color: '#94A3B8',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        display: i > 1 ? { xs: 'none', sm: 'block' } : 'block',
                                    }}
                                >
                                    {h}
                                </Typography>
                            ))}
                        </Box>

                        {retreatSchedule.addons.map((addon, idx) => (
                            <Box key={addon.id}>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr auto', sm: '1fr auto auto auto' },
                                        px: cardPx,
                                        py: 1.75,
                                        gap: { xs: 1, sm: 2 },
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    {/* Name + description */}
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                            {addon.addOnName || addon.ownAddonName || addon.addonName || 'Unnamed Add-on'}
                                        </Typography>
                                        {addon.description && (
                                            <Typography variant="caption" sx={{ color: '#94A3B8', lineHeight: 1.4 }}>
                                                {addon.description}
                                            </Typography>
                                        )}
                                        {/* Mobile-only: show Qty + badge inline */}
                                        <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 1, mt: 0.5, alignItems: 'center' }}>
                                            {addon.quantity != null && (
                                                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                                    Qty: {addon.quantity}
                                                </Typography>
                                            )}
                                            <Chip
                                                label={addon.isCompulsory ? 'Required' : 'Optional'}
                                                size="small"
                                                sx={{
                                                    height: 18, fontSize: '0.65rem', fontWeight: 600,
                                                    bgcolor: addon.isCompulsory ? '#FFFBEB' : '#F8FAFC',
                                                    color: addon.isCompulsory ? '#92400E' : '#64748B',
                                                    border: `1px solid ${addon.isCompulsory ? '#FDE68A' : '#E2E8F0'}`,
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Price */}
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', whiteSpace: 'nowrap' }}>
                                        {retreatSchedule.currency} {addon.price}
                                    </Typography>

                                    {/* Qty — desktop only */}
                                    <Typography variant="body2" sx={{ color: '#64748B', display: { xs: 'none', sm: 'block' } }}>
                                        {addon.quantity != null ? addon.quantity : '—'}
                                    </Typography>

                                    {/* Type badge — desktop only */}
                                    <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                                        <Chip
                                            label={addon.isCompulsory ? 'Required' : 'Optional'}
                                            size="small"
                                            sx={{
                                                height: 20, fontSize: '0.68rem', fontWeight: 600,
                                                bgcolor: addon.isCompulsory ? '#FFFBEB' : '#F8FAFC',
                                                color: addon.isCompulsory ? '#92400E' : '#64748B',
                                                border: `1px solid ${addon.isCompulsory ? '#FDE68A' : '#E2E8F0'}`,
                                            }}
                                        />
                                    </Box>
                                </Box>
                                {idx < retreatSchedule.addons.length - 1 && <Divider />}
                            </Box>
                        ))}
                    </InfoCard>
                </Box>
            )}

        </Box>
    );
};

// ─── Helper sub-component for period offer chips ──────────────────────────────
const PeriodOffers = ({ date }) => (
    <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {date.earlyBirdEnabled && (
            <Chip
                icon={<Tag size={10} />}
                label={
                    date.earlyBirdLastDate
                        ? `Early Bird ends ${formatDate(date.earlyBirdLastDate)}`
                        : date.earlyBirdDaysBefore
                            ? `Early Bird: ${date.earlyBirdDaysBefore}d before`
                            : 'Early Bird Active'
                }
                size="small"
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#FFFBEB', color: '#92400E', border: '1px solid #FDE68A' }}
            />
        )}
        {date.flashSaleEnabled && (
            <Chip
                icon={<Zap size={10} />}
                label={
                    date.flashSaleStartDate
                        ? `Flash ${formatDate(date.flashSaleStartDate)} – ${formatDate(date.flashSaleLastDate)}`
                        : 'Flash Sale Active'
                }
                size="small"
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#ECFEFF', color: '#164E63', border: '1px solid #A5F3FC' }}
            />
        )}
        {!date.earlyBirdEnabled && !date.flashSaleEnabled && (
            <Typography variant="caption" sx={{ color: '#94A3B8' }}>Standard pricing</Typography>
        )}
    </Stack>
);

export default SchedulePricingTab;
